import PageLoader from '@/common/service/flow/page-loader';
import { loadSelectByPersist, plansDataMapper, filterCapacity } from '@/common/service/plan-data-mapper';
import { pageConfig, flowTypes } from '@/common/service/page-config';
import Vue from 'vue';
import { isDebug, isEmptyVal } from '@/utils';
import store from '@/store/index';
import _, { cloneDeep } from 'lodash';
import { addToCartByCategoryAndBillingCycle, planQueryParser } from '@/common/service/flow/utils';
import { addParamsIfApm } from '@/common/service/checkout-utils';
import { pollingEventStatus } from '@/common/polling';
import {
  buyTrialSuccessPage,
  addTrialSuccessPage,
  gotoSuccessPage,
  gotoWiseSuccessPage,
} from '@/common/service/common';
import { BILLING_CYCLE, DEFAULT_FREE_TRIAL_DAYS, planTypes, zoomOneProMonthlyPlanCodes, zoomOneProYearlyPlanCodes } from '@/common/constants';
import { VIEWS } from '@/views/bridge';
import Flow from '@/flows/flow';
import {
  getFreeTrialPlansByCampaignName,
  buyFreeTrialPlansForFreeUser,
  buyFreeTrialPlans,
  getPlanMap,
  capacityConfig,
} from '@/apis';
import { getPlanTypeByCategory } from '@/common/service/parse';
import { validAddonCycleForBase } from '@/common/service/validate/billing-cycle';
import { APM, WISE } from '@/views/checkout/payment/helper/constant';
import { removeEmptyValue } from '@/common/service/checkout-utils';
import { translation } from '@/plugins/i18n';

function removeUrlParam(url, paramName) {
  try {
    const urlParts = url.split('?');
    if (urlParts.length >= 2) {
      let prefix = encodeURIComponent(paramName) + '=';
      let params = urlParts[1].split(/[&;]/g);

      for (let i = params.length; i-- > 0; ) {
        if (params[i].lastIndexOf(prefix, 0) !== -1) {
          params.splice(i, 1);
        }
      }

      url = urlParts[0] + (params.length > 0 ? '?' + params.join('&') : '');
    }
    return url;
  } catch (_) {
    return url;
  }
}

export const freeTrialIneligibleCheck = () => {
  const isLoggedIn = window?.SB?.loggedIn;
  const { freeTrialErrorType = '', from = '', from_sub = '', plan = '' } = pageConfig || {};
  if (!freeTrialErrorType) {
    return null;
  }
  try {
    const planType = getPlanTypeByCategory(from || from_sub || plan);
    let errorMessage = '';
    const planTypeTitleMap = {
      [planTypes.ZOOM_MEETING]: 'billing.planform.title.zoom_one_pro',
      [planTypes.ZOOM_SCHEDULER]: 'billing.scheduler.title',
      [planTypes.EVENT_SAAS_CORE]: 'billing.planform.title.eventsaascore',
    };
    const freeTrialTitle = planTypeTitleMap[planType]
      ? translation('billing.trial.title', [translation(planTypeTitleMap[planType])])
      : '';
    const title = planTypeTitleMap[planType] ? translation(planTypeTitleMap[planType]) : '';
    const user = window.SB?.user?.email || '';
    errorMessage = isLoggedIn
      ? translation('billing.trial.ineligible_hint', [user, freeTrialTitle, title])
      : translation('billing.trial.ineligible_hint_nn', [translation(planTypeTitleMap[planType])]);
    Vue.prototype.$message.error({
      duration: 50000,
      message: errorMessage,
      showClose: true,
      dangerouslyUseHTMLString: true,
    });
    // clear mark
    const newUrl = removeUrlParam(window?.location?.href || '', 'freeTrialErrorType');
    if (newUrl) {
      window?.history?.pushState({}, '', newUrl);
    }
  } catch (e) {
    // no-op
  }

  return true;
};

const freeTrialErrorRedirection = (planType, flowType) => {
  switch (planType) {
    case planTypes.ZOOM_SCHEDULER:
      return `/opc/buy?plan=zsched&period=monthly&from=zsched&originalFlowType=${flowType}&freeTrialErrorType=zsched`;
    case planTypes.ZOOM_MEETING:
      return `/opc/buy/config?plan=pro&subPlan=allSubPlan&type=basic2pro&originalFlowType=${flowType}&freeTrialErrorType=pro`;
    default:
      return '/billing';
  }
};

export const freeTrialErrorMessage = (errorMessage = '', flowType = flowTypes.buyTrial) => {
  const { from = '', from_sub = '' } = pageConfig || {};
  try {
    const planType = getPlanTypeByCategory(from || from_sub);
    location.replace(freeTrialErrorRedirection(planType, flowType));
  } catch (e) {
    Vue.prototype.$message({
      type: 'info',
      duration: 50000,
      message: errorMessage,
      showClose: true,
      iconClass: 'zm-icon-info',
      dangerouslyUseHTMLString: true,
      customClass: 'opc-global-info-message',
    });
  }
};

const checkSelectedPlan = (planMapFlat = []) => {
  return planMapFlat.find((item) => item.cartInfo.selected_number > 0);
};

const filterDisabledPlans = (allPlanResult = {}) => {
  const { disablePlanCodeList = [], disablePlanCategoryList = [] } = allPlanResult;

  return (planMapFlat = []) => {
    return planMapFlat.filter(
      (p) => !disablePlanCodeList.includes(p?.plan_code) && !disablePlanCategoryList.includes(p?.parent_category),
    );
  };
};

const filterFreeTrialPlansFromPlanMap = (freeTrialPlanList = []) => {
  return (planList = []) => {
    return planList.map((item) => {
      const plan = { ...item };
      // filter all plans with free trial in freeTrialPlanList
      const freeTrialPlan = freeTrialPlanList.find((free) => {
        return [plan.plan_code, plan?.priceChangeMappingPlanCode].includes(free.planCode);
      });
      if (freeTrialPlan) {
        plan.isOnlineFreeTrial = true;
        plan.trialDays = freeTrialPlan.trialDays || DEFAULT_FREE_TRIAL_DAYS;
        plan.trialCode = freeTrialPlan.trialCode || '';
      }
      return plan;
    });
  };
};

const loadDefaultSelectByQuery = () => {
  return (planList = []) => {
    const hasSelectedPlan = checkSelectedPlan(planList);
    if (hasSelectedPlan) return planList;
    let { from, from_sub, period = BILLING_CYCLE.ANNUAL, quantity = 1 } = pageConfig || {};
    const freeTrialPlanList = planList.filter((p) => !!p.isOnlineFreeTrial); // filter free trial plan only
    const { category_codes } = planQueryParser(from || from_sub);

    // add target plan to shopping cart
    category_codes.forEach((category) => {
      addToCartByCategoryAndBillingCycle(
        freeTrialPlanList,
        getPlanTypeByCategory(category),
        category,
        period,
        quantity,
      );
    });
    return planList;
  };
};

const initShoppingCart = async (state) => {
  const { persist } = state;
  // load data
  const { result: planMap = {} } = await getPlanMap();
  const { result: capacityList } = await capacityConfig({ flowType: flowTypes.buyTrial });
  const { campaign_code = '' } = pageConfig || {};
  const { result: freeTrialPlanList = [], errorMessage = '' } = await getFreeTrialPlansByCampaignName(campaign_code);
  // Just warning, won't block page loading.
  if (errorMessage) {
    freeTrialErrorMessage(errorMessage);
  }

  const loader = new PageLoader(planMap);
  loader.decorate('plansDataMapper', plansDataMapper);
  loader.decorate('filterDisabledPlans', filterDisabledPlans(planMap));
  loader.decorate('filterCapacity', filterCapacity(capacityList));
  loader.decorate('filterFreeTrialPlansFromPlanMap', filterFreeTrialPlansFromPlanMap(freeTrialPlanList));
  loader.decorate('loadByPersist', loadSelectByPersist(persist));
  loader.decorate('loadDefaultSelectByQuery', loadDefaultSelectByQuery());
  const planList = await loader.dispatch();

  return {
    allPlanMap: planMap,
    planMapFlat: planList,
  };
};

const checkOutSubmit = async () => {
  const { campaign_code: campaignCode = '' } = pageConfig || {};
  const getters = store.getters;
  const addressInfo = getters.addressInfo;
  const { apmInfo } = getters;
  const preview = getters.isLoadingPreview;
  const payload = store.getters.orderDataPayload;
  let { paymentResponse, paymentGateway } = getters.paymentMethodInfo;
  const freeTrialPlan = payload?.planList?.find((plan) => !!plan.trialCode);
  const isProFreeTrial = [...zoomOneProMonthlyPlanCodes, ...zoomOneProYearlyPlanCodes].includes(freeTrialPlan?.planCode);
  if (apmInfo.isApm) {
    paymentResponse = JSON.stringify({
      integrationType: APM,
    });
    const eventResult = await pollingEventStatus({ rps: true });
    const successParams = {
      integrationType: APM,
      apmMethodName: apmInfo.apmPaymentMethodName,
    };
    apmInfo.apmPaymentMethodName === WISE
      ? gotoWiseSuccessPage({ wiseStateValue: apmInfo.wiseStateValue })
      : gotoSuccessPage(successParams);
    return {
      eventResult,
    };
  }

  // if the value is empty, will not send this field
  const extensionData = _.omitBy({
    freeTrialInstanceId: pageConfig?.ftId
  }, isEmptyVal);

  let params = {
    taxInfo: getters.taxInfo,
    contact: cloneDeep([removeEmptyValue(getters.billToContact), removeEmptyValue(getters.soldToContact)]),
    paymentInfo: { paymentResponse, paymentGateway },
    orderData: payload,
    extension: {
      usageType: addressInfo?.usageType,
      useCase: addressInfo?.useCase,
      otherUseCase: addressInfo?.otherUseCase,
      campaignCode,
      trialCode: freeTrialPlan?.trialCode || '',
      ...extensionData,
    },
    preview,
  };

  isDebug && console.log('buy trial submit params: ', params);
  const isLoggedIn = window.SB?.loggedIn;
  addParamsIfApm(params);
  isLoggedIn ? await buyFreeTrialPlansForFreeUser(params) : await buyFreeTrialPlans(params);
  const eventResult = await pollingEventStatus({ rps: true });
  !isLoggedIn || isProFreeTrial /* Pro free trial only allowed the free users to purchase and couldn't be ADD flow */ ? buyTrialSuccessPage() : addTrialSuccessPage();

  return {
    eventResult,
  };
};

const resolveView = () => {
  return VIEWS.CONFIG;
};

const preCheck = async () => {
  // FREE TRIAL - check billing cycle same with base plan for future auto upgrade
  await validAddonCycleForBase();
};

export default {
  [flowTypes.buyTrial]: class extends Flow {
    view() {
      return resolveView();
    }
    init() {
      return initShoppingCart(...arguments);
    }
    beforeSubmit() {
      return preCheck();
    }
    submit() {
      return checkOutSubmit();
    }
  },
};
